import React, { useState, useEffect, useCallback } from 'react';
import {
    Tabs, Card, Table, Tag, Button, Drawer, Form, Input, InputNumber,
    Select, Space, Divider, message, Modal, Typography, Row, Col, Badge, Empty,
    Descriptions, Statistic, Alert
} from 'antd';
import {
    PlusOutlined, ToolOutlined, CheckCircleOutlined, DollarOutlined,
    ReloadOutlined, EyeOutlined, ShoppingCartOutlined, ShopOutlined
} from '@ant-design/icons';
import axios from 'axios';
import useProductStore from '../store/useProductStore';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const api = (path, method = 'GET', data = null) => {
    const token = localStorage.getItem('token');
    return axios({
        method, url: `/api${path}`, data,
        headers: { Authorization: `Bearer ${token}` }
    });
};

// ── Status helpers ────────────────────────────────────────────────────────────

const STATUS_META = {
    purchased: { color: 'orange', label: 'Purchased', icon: <ShoppingCartOutlined /> },
    maintenance: { color: 'blue', label: 'Maintenance', icon: <ToolOutlined /> },
    ready: { color: 'green', label: 'Ready', icon: <CheckCircleOutlined /> },
    sold: { color: 'default', label: 'Sold', icon: <DollarOutlined /> },
};

const StatusBadge = ({ status }) => {
    const meta = STATUS_META[status] || { color: 'default', label: status };
    return <Tag color={meta.color}>{meta.label}</Tag>;
};

// ── Used Products Tab ─────────────────────────────────────────────────────────

const UsedProductsTab = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState('');

    // Drawers
    const [addDrawer, setAddDrawer] = useState(false);
    const [maintDrawer, setMaintDrawer] = useState(false);
    const [readyDrawer, setReadyDrawer] = useState(false);
    const [sellModal, setSellModal] = useState(false);
    const [detailModal, setDetailModal] = useState(false);
    const [selected, setSelected] = useState(null);

    const [addForm] = Form.useForm();
    const [maintForm] = Form.useForm();
    const [readyForm] = Form.useForm();
    const [sellForm] = Form.useForm();
    const [saving, setSaving] = useState(false);

    const fetchProducts = useProductStore(state => state.fetchProducts);

    const fetchItems = useCallback(async () => {
        setLoading(true);
        try {
            const params = statusFilter ? { status: statusFilter } : {};
            const res = await api('/used-items?' + new URLSearchParams(params));
            setItems(res.data);
        } catch { message.error('Failed to load used items'); }
        finally { setLoading(false); }
    }, [statusFilter]);

    useEffect(() => { fetchItems(); }, [fetchItems]);

    // ── Actions ────────────────────────────────────────────────────────────

    const handleCreate = async (values) => {
        setSaving(true);
        try {
            await api('/used-items', 'POST', values);
            message.success('Item added successfully');
            addForm.resetFields();
            setAddDrawer(false);
            fetchItems();
        } catch (e) { message.error(e.response?.data?.error || 'Failed to create item'); }
        finally { setSaving(false); }
    };

    const handleMaintenance = async (values) => {
        setSaving(true);
        try {
            await api(`/used-items/${selected.id}/maintenance`, 'PUT', values);
            message.success('Moved to Maintenance');
            maintForm.resetFields();
            setMaintDrawer(false);
            fetchItems();
        } catch (e) { message.error(e.response?.data?.error || 'Failed to update'); }
        finally { setSaving(false); }
    };

    const handleReady = async (values) => {
        setSaving(true);
        try {
            await api(`/used-items/${selected.id}/ready`, 'PUT', values);
            message.success('Item marked as Ready & synced to Dolibarr');
            readyForm.resetFields();
            setReadyDrawer(false);
            fetchItems();
            // Sync POS store so item appears in catalog
            fetchProducts().catch(() => { });
        } catch (e) { message.error(e.response?.data?.error || 'Failed to update'); }
        finally { setSaving(false); }
    };

    const handleSell = async (values) => {
        setSaving(true);
        try {
            await api(`/used-items/${selected.id}/sell`, 'POST', values);
            message.success('Item sold — Invoice created in Dolibarr');
            sellForm.resetFields();
            setSellModal(false);
            fetchItems();
            // Sync POS store so item disappears from catalog
            fetchProducts().catch(() => { });
        } catch (e) { message.error(e.response?.data?.error || 'Failed to sell item'); }
        finally { setSaving(false); }
    };

    const openAction = (item, action) => {
        setSelected(item);
        if (action === 'maintenance') { maintForm.resetFields(); setMaintDrawer(true); }
        if (action === 'ready') { readyForm.setFieldValue('sale_price', item.sale_price || 0); setReadyDrawer(true); }
        if (action === 'sell') { sellForm.setFieldValue('sold_price', item.sale_price || 0); setSellModal(true); }
        if (action === 'detail') { setDetailModal(true); }
    };

    const ActionButton = ({ item }) => {
        if (item.status === 'purchased') return (
            <Button size="small" icon={<ToolOutlined />} onClick={() => openAction(item, 'maintenance')}>
                Maintenance
            </Button>
        );
        if (item.status === 'maintenance') return (
            <Button size="small" type="primary" icon={<CheckCircleOutlined />} onClick={() => openAction(item, 'ready')}>
                Mark Ready
            </Button>
        );
        if (item.status === 'ready') return (
            <Button size="small" type="primary" danger icon={<DollarOutlined />} onClick={() => openAction(item, 'sell')}>
                Sell
            </Button>
        );
        return <Tag color="default">Sold</Tag>;
    };

    const columns = [
        {
            title: 'Title', dataIndex: 'title', key: 'title', render: (t, r) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{t}</Text>
                    {r.serial_number && <Text type="secondary" style={{ fontSize: 11 }}>SN: {r.serial_number}</Text>}
                </Space>
            )
        },
        { title: 'Category', dataIndex: 'category', key: 'category', render: v => v || '—' },
        { title: 'Status', dataIndex: 'status', key: 'status', render: s => <StatusBadge status={s} /> },
        {
            title: 'Purchase Price', dataIndex: 'purchase_price', key: 'purchase_price',
            render: v => `${parseFloat(v || 0).toFixed(3)} JOD`, align: 'right'
        },
        {
            title: 'Sale Price', dataIndex: 'sale_price', key: 'sale_price',
            render: v => v > 0 ? `${parseFloat(v).toFixed(3)} JOD` : '—', align: 'right'
        },
        { title: 'Seller', dataIndex: 'seller_name', key: 'seller_name', render: v => v || '—' },
        {
            title: 'Actions', key: 'actions', align: 'center', render: (_, record) => (
                <Space>
                    <Button size="small" icon={<EyeOutlined />} onClick={() => openAction(record, 'detail')} />
                    <ActionButton item={record} />
                </Space>
            )
        },
    ];

    const fmt = v => `${parseFloat(v || 0).toFixed(3)} JOD`;

    return (
        <div>
            {/* Toolbar */}
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                <Col>
                    <Space>
                        <Select
                            value={statusFilter}
                            onChange={setStatusFilter}
                            style={{ width: 160 }}
                            placeholder="Filter by status"
                            allowClear
                            onClear={() => setStatusFilter('')}
                        >
                            <Option value="purchased">Purchased</Option>
                            <Option value="maintenance">Maintenance</Option>
                            <Option value="ready">Ready</Option>
                            <Option value="sold">Sold</Option>
                        </Select>
                        <Button icon={<ReloadOutlined />} onClick={fetchItems} />
                    </Space>
                </Col>
                <Col>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddDrawer(true)}>
                        Add Used Item
                    </Button>
                </Col>
            </Row>

            <Table
                columns={columns}
                dataSource={items}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 15 }}
                locale={{ emptyText: <Empty description="No used items found" /> }}
                size="middle"
            />

            {/* ── Add Item Drawer ── */}
            <Drawer title="Add Used Item (Purchased)" open={addDrawer} onClose={() => setAddDrawer(false)} width={480} footer={null}>
                <Form form={addForm} layout="vertical" onFinish={handleCreate}>
                    <Form.Item name="title" label="Title / Description" rules={[{ required: true }]}>
                        <Input placeholder="e.g. iPhone 13 Pro 256GB" />
                    </Form.Item>
                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item name="category" label="Category">
                                <Input placeholder="e.g. Smartphone" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="serial_number" label="Serial / IMEI">
                                <Input placeholder="Optional" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item name="purchase_price" label="Purchase Price (JOD)" rules={[{ required: true }]}>
                                <InputNumber min={0} step={0.001} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="seller_name" label="Seller Name">
                                <Input placeholder="Optional" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item name="notes" label="Notes">
                        <TextArea rows={3} />
                    </Form.Item>
                    <Divider />
                    <Button type="primary" htmlType="submit" loading={saving} block>Add Item</Button>
                </Form>
            </Drawer>

            {/* ── Maintenance Drawer ── */}
            <Drawer
                title={`Maintenance — ${selected?.title || ''}`}
                open={maintDrawer}
                onClose={() => setMaintDrawer(false)}
                width={420}
                footer={null}
            >
                <Alert message="Enter repair costs. Item will move to 'Maintenance' status." type="info" showIcon style={{ marginBottom: 16 }} />
                <Form form={maintForm} layout="vertical" onFinish={handleMaintenance}>
                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item name="maintenance_cost" label="Parts Cost (JOD)" initialValue={0}>
                                <InputNumber min={0} step={0.001} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="labor_cost" label="Labor Cost (JOD)" initialValue={0}>
                                <InputNumber min={0} step={0.001} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item name="repair_notes" label="Repair Notes">
                        <TextArea rows={4} />
                    </Form.Item>
                    <Divider />
                    <Button type="primary" htmlType="submit" loading={saving} block icon={<ToolOutlined />}>
                        Move to Maintenance
                    </Button>
                </Form>
            </Drawer>

            {/* ── Mark Ready Drawer ── */}
            <Drawer
                title={`Mark Ready — ${selected?.title || ''}`}
                open={readyDrawer}
                onClose={() => setReadyDrawer(false)}
                width={420}
                footer={null}
            >
                {selected && (
                    <Row gutter={16} style={{ marginBottom: 16 }}>
                        <Col span={8}><Statistic title="Purchase" value={fmt(selected.purchase_price)} /></Col>
                        <Col span={8}><Statistic title="Parts" value={fmt(selected.maintenance_cost)} /></Col>
                        <Col span={8}><Statistic title="Labor" value={fmt(selected.labor_cost)} /></Col>
                    </Row>
                )}
                <Alert
                    message={`Total Cost: ${fmt((parseFloat(selected?.purchase_price) || 0) + (parseFloat(selected?.maintenance_cost) || 0) + (parseFloat(selected?.labor_cost) || 0))}`}
                    type="success" showIcon style={{ marginBottom: 16 }}
                />
                <Alert message="When saved, a product will be created in Dolibarr." type="info" showIcon style={{ marginBottom: 16 }} />
                <Form form={readyForm} layout="vertical" onFinish={handleReady}>
                    <Form.Item name="final_condition" label="Final Condition" rules={[{ required: true }]}>
                        <Select>
                            <Option value="A">A — Excellent</Option>
                            <Option value="B">B — Good</Option>
                            <Option value="C">C — Fair</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="sale_price" label="Sale Price (JOD)" rules={[{ required: true }]}>
                        <InputNumber min={0} step={0.001} style={{ width: '100%' }} />
                    </Form.Item>
                    <Divider />
                    <Button type="primary" htmlType="submit" loading={saving} block icon={<CheckCircleOutlined />}>
                        Mark as Ready
                    </Button>
                </Form>
            </Drawer>

            {/* ── Sell Modal ── */}
            <Modal
                title={`Sell — ${selected?.title || ''}`}
                open={sellModal}
                onCancel={() => setSellModal(false)}
                footer={null}
            >
                <Alert message="A sales invoice will be created in Dolibarr upon confirmation." type="warning" showIcon style={{ marginBottom: 16 }} />
                <Form form={sellForm} layout="vertical" onFinish={handleSell}>
                    <Form.Item name="sold_price" label="Sold Price (JOD)" rules={[{ required: true }]}>
                        <InputNumber min={0} step={0.001} style={{ width: '100%' }} />
                    </Form.Item>
                    <Divider />
                    <Button type="primary" danger htmlType="submit" loading={saving} block icon={<DollarOutlined />}>
                        Confirm Sale
                    </Button>
                </Form>
            </Modal>

            {/* ── Detail Modal ── */}
            <Modal
                title={`Item Details — ${selected?.title || ''}`}
                open={detailModal}
                onCancel={() => setDetailModal(false)}
                footer={<Button onClick={() => setDetailModal(false)}>Close</Button>}
                width={620}
            >
                {selected && (
                    <Descriptions bordered column={2} size="small">
                        <Descriptions.Item label="Title" span={2}>{selected.title}</Descriptions.Item>
                        <Descriptions.Item label="Category">{selected.category || '—'}</Descriptions.Item>
                        <Descriptions.Item label="Serial / IMEI">{selected.serial_number || '—'}</Descriptions.Item>
                        <Descriptions.Item label="Status" span={2}><StatusBadge status={selected.status} /></Descriptions.Item>
                        <Descriptions.Item label="Seller">{selected.seller_name || '—'}</Descriptions.Item>
                        <Descriptions.Item label="Condition">{selected.final_condition || '—'}</Descriptions.Item>
                        <Descriptions.Item label="Purchase Price">{fmt(selected.purchase_price)}</Descriptions.Item>
                        <Descriptions.Item label="Maintenance Cost">{fmt(selected.maintenance_cost)}</Descriptions.Item>
                        <Descriptions.Item label="Labor Cost">{fmt(selected.labor_cost)}</Descriptions.Item>
                        <Descriptions.Item label="Total Cost">{fmt(selected.total_cost)}</Descriptions.Item>
                        <Descriptions.Item label="Sale Price">{fmt(selected.sale_price)}</Descriptions.Item>
                        <Descriptions.Item label="Sold Price">{selected.sold_price ? fmt(selected.sold_price) : '—'}</Descriptions.Item>
                        <Descriptions.Item label="Dolibarr Product" span={2}>{selected.dolibarr_reference || '—'}</Descriptions.Item>
                        <Descriptions.Item label="Dolibarr Invoice" span={2}>{selected.dolibarr_invoice_id || '—'}</Descriptions.Item>
                        {selected.notes && <Descriptions.Item label="Notes" span={2}>{selected.notes}</Descriptions.Item>}
                        {selected.repair_notes && <Descriptions.Item label="Repair Notes" span={2}>{selected.repair_notes}</Descriptions.Item>}
                        <Descriptions.Item label="Created">{new Date(selected.created_at).toLocaleString()}</Descriptions.Item>
                        <Descriptions.Item label="Updated">{new Date(selected.updated_at).toLocaleString()}</Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>
        </div>
    );
};

// ── New Products Tab (Dolibarr Purchase Flow) ─────────────────────────────────

const NewProductsTab = () => {
    const [form] = Form.useForm();
    const [addSupplierForm] = Form.useForm();
    const fetchProducts = useProductStore(state => state.fetchProducts);

    const [products, setProducts] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [productsLoading, setProductsLoading] = useState(false);
    const [suppliersLoading, setSuppliersLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [addSupplierOpen, setAddSupplierOpen] = useState(false);
    const [addingSupplier, setAddingSupplier] = useState(false);

    const loadProducts = async () => {
        setProductsLoading(true);
        try {
            const res = await api('/products');
            // Exclude USED-* products: they are tracked via the Used Items lifecycle
            // (PostgreSQL purchase_items) and must go through the Used Products tab.
            // Purchasing them here would update Dolibarr stock_reel but NOT the POS
            // stock source (stockMap), so they would never appear in POS.
            const filtered = (res.data || []).filter(
                p => !(typeof p.ref === 'string' && p.ref.startsWith('USED-'))
            );
            setProducts(filtered);
        } catch { message.error('Failed to load products'); }
        finally { setProductsLoading(false); }
    };

    const loadSuppliers = async (search = '') => {
        setSuppliersLoading(true);
        try {
            const q = search ? `?search=${encodeURIComponent(search)}` : '';
            const res = await api(`/suppliers${q}`);
            setSuppliers(res.data || []);
        } catch { message.error('Failed to load suppliers'); }
        finally { setSuppliersLoading(false); }
    };

    useEffect(() => {
        loadProducts();
        loadSuppliers();
    }, []);

    const handleSupplierSearch = useCallback(
        (() => {
            let timer;
            return (val) => {
                clearTimeout(timer);
                timer = setTimeout(() => loadSuppliers(val), 400);
            };
        })(),
        []
    );

    const handleAddSupplier = async (values) => {
        setAddingSupplier(true);
        try {
            const res = await api('/suppliers', 'POST', values);
            message.success(`Supplier "${res.data.name}" created`);
            addSupplierForm.resetFields();
            setAddSupplierOpen(false);
            await loadSuppliers();
            form.setFieldValue('supplier_id', res.data.id);
        } catch (e) {
            message.error(e.response?.data?.details || e.response?.data?.error || 'Failed to create supplier');
        } finally { setAddingSupplier(false); }
    };

    const handleSubmit = async (values) => {
        setSaving(true);
        try {
            const result = await api('/purchase/new', 'POST', values);
            const warning = result.data?.warning;
            if (warning) {
                message.warning(`Purchase created, but: ${warning}`);
            } else {
                message.success('Purchase created — stock updated in Dolibarr');
            }
            form.resetFields();
            // Refresh the shared product store so POS catalog reflects the new stock immediately
            fetchProducts().catch(() => { });
        } catch (e) {
            message.error(e.response?.data?.details || e.response?.data?.error || 'Failed to create purchase');
        } finally { setSaving(false); }
    };

    return (
        <Card style={{ maxWidth: 620 }}>
            <Title level={5} style={{ marginBottom: 20 }}>New Product Purchase</Title>
            <Alert
                message="For new / regular products from suppliers only."
                description={<>To add used devices to the POS, use the <strong>Used Products</strong> tab instead — they require a separate lifecycle (purchased → maintenance → ready).</>}
                type="info" showIcon style={{ marginBottom: 20 }}
            />
            <Form form={form} layout="vertical" onFinish={handleSubmit}>
                {/* Product */}
                <Form.Item name="product_id" label="Product" rules={[{ required: true, message: 'Please select a product' }]}>
                    <Select
                        showSearch
                        placeholder="Search and select a product…"
                        optionFilterProp="children"
                        loading={productsLoading}
                        notFoundContent={
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description="No products found"
                            />
                        }
                    >
                        {products.map(p => (
                            <Option key={p.id} value={p.id}>
                                {p.label} <Text type="secondary">({p.ref})</Text>
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                {/* Qty + Price */}
                <Row gutter={12}>
                    <Col span={12}>
                        <Form.Item name="qty" label="Quantity" rules={[{ required: true, message: 'Required' }]}>
                            <InputNumber min={1} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="price" label="Unit Price (JOD)" rules={[{ required: true, message: 'Required' }]}>
                            <InputNumber min={0} step={0.001} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>

                {/* Supplier */}
                <Form.Item
                    label={
                        <Space>
                            Supplier
                            <Button
                                type="link"
                                size="small"
                                icon={<PlusOutlined />}
                                style={{ padding: 0, height: 'auto' }}
                                onClick={() => { addSupplierForm.resetFields(); setAddSupplierOpen(true); }}
                            >
                                Add Supplier
                            </Button>
                        </Space>
                    }
                    name="supplier_id"
                    rules={[{ required: true, message: 'Please select a supplier' }]}
                >
                    <Select
                        showSearch
                        placeholder="Search suppliers by name, code, phone…"
                        loading={suppliersLoading}
                        onSearch={handleSupplierSearch}
                        filterOption={false}
                        notFoundContent={
                            suppliersLoading ? null : (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={
                                        <span>
                                            No suppliers found.{' '}
                                            <Button
                                                type="link"
                                                size="small"
                                                style={{ padding: 0 }}
                                                onClick={() => { addSupplierForm.resetFields(); setAddSupplierOpen(true); }}
                                            >
                                                Create one
                                            </Button>
                                        </span>
                                    }
                                />
                            )
                        }
                    >
                        {suppliers.map(s => (
                            <Option key={s.id} value={s.id}>
                                <Space>
                                    <Text strong>{s.name}</Text>
                                    {s.code && <Text type="secondary" style={{ fontSize: 11 }}>({s.code})</Text>}
                                    {s.phone && <Text type="secondary" style={{ fontSize: 11 }}>· {s.phone}</Text>}
                                    {s.status === 'inactive' && <Tag color="default" style={{ fontSize: 10 }}>Inactive</Tag>}
                                </Space>
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item name="notes" label="Notes">
                    <TextArea rows={2} placeholder="Purchase notes…" />
                </Form.Item>

                <Divider />
                <Button type="primary" htmlType="submit" loading={saving} block icon={<ShopOutlined />}>
                    Create Purchase
                </Button>
            </Form>

            {/* Quick-Add Supplier Drawer */}
            <Drawer
                title="Quick Add Supplier"
                open={addSupplierOpen}
                onClose={() => setAddSupplierOpen(false)}
                width={420}
                footer={null}
            >
                <Alert
                    message="The new supplier will be created in Dolibarr and auto-selected."
                    type="info" showIcon style={{ marginBottom: 16 }}
                />
                <Form form={addSupplierForm} layout="vertical" onFinish={handleAddSupplier}>
                    <Form.Item name="name" label="Supplier Name" rules={[{ required: true }]}>
                        <Input placeholder="e.g. ABC Trading Co." />
                    </Form.Item>
                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item name="email" label="Email"
                                rules={[{ type: 'email', message: 'Invalid email address' }]}>
                                <Input type="email" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="phone" label="Phone">
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item name="city" label="City">
                        <Input />
                    </Form.Item>
                    <Divider />
                    <Button type="primary" htmlType="submit" loading={addingSupplier} block icon={<PlusOutlined />}>
                        Create Supplier
                    </Button>
                </Form>
            </Drawer>
        </Card>
    );
};


// ── Main Page ─────────────────────────────────────────────────────────────────

const PurchaseProducts = () => {
    const tabItems = [
        {
            key: 'used',
            label: (
                <span>
                    <RetweetOutlined style={{ marginRight: 6 }} />
                    Used Products
                </span>
            ),
            children: <UsedProductsTab />,
        },
        {
            key: 'new',
            label: (
                <span>
                    <ShopOutlined style={{ marginRight: 6 }} />
                    New Products
                </span>
            ),
            children: <NewProductsTab />,
        },
    ];

    return (
        <Card
            title={<Title level={4} style={{ margin: 0 }}>Purchase Products</Title>}
            bodyStyle={{ paddingTop: 0 }}
        >
            <Tabs defaultActiveKey="used" items={tabItems} />
        </Card>
    );
};

// Fix missing import
function RetweetOutlined(props) {
    return (
        <span role="img" aria-label="retweet" className="anticon" {...props}>
            <svg viewBox="64 64 896 896" width="1em" height="1em" fill="currentColor">
                <path d="M136 552h63.6c4.4 0 8-3.6 8-8V288.7h528.6v72.6c0 1.9.6 3.7 1.8 5.1a8.3 8.3 0 0011.7.9l187.5-153.1c2.2-1.7 2.8-4.9 1.2-7.3l-1.2-1.2-187.5-153c-1.5-1.2-3.3-1.8-5.1-1.8-4.6 0-8.3 3.7-8.3 8.3V136H240c-44.2 0-80 35.8-80 80v328c0 4.4 3.6 8 8 8zm752-80h-63.6c-4.4 0-8 3.6-8 8v255.3H287.8v-72.6c0-1.9-.6-3.7-1.8-5.1a8.3 8.3 0 00-11.7-.9L86.8 810.8c-2.2 1.7-2.8 4.9-1.2 7.3l1.2 1.2 187.5 153c1.5 1.2 3.3 1.8 5.1 1.8 4.6 0 8.3-3.7 8.3-8.3V888H784c44.2 0 80-35.8 80-80V480c0-4.4-3.6-8-8-8z" />
            </svg>
        </span>
    );
}

export default PurchaseProducts;
